import { useEffect, useMemo, Suspense } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, type AppRole } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  LayoutDashboard,
  Users,
  LogOut,
  Loader2,
  Home,
  Menu,
  X,
  Building2,
  Settings,
  Ticket,
  ChevronDown,
  Bed,
  RefreshCw,
  UtensilsCrossed,
  UserPlus,
  ClipboardList,
  ClipboardCheck,
  CalendarDays,
  FileBarChart,
  FileText,
  CreditCard,
  User,
  MonitorDot,
  Gift,
  Radio,
  BarChart3,
  Package,
  AlertTriangle,
  Printer,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import logo from "@/assets/logo.png";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { NotificationBell } from "./NotificationBell";
import { useOpenAnomalies } from "@/hooks/useOpenAnomalies";

type NavItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: { href: string; label: string; icon: React.ComponentType<{ className?: string }> }[];
};

const allNavItems: NavItem[] = [
  { href: "/admin", label: "Recepción", icon: MonitorDot },
  { href: "/admin/estadisticas", label: "Estadísticas", icon: LayoutDashboard },
  { 
    href: "/admin/hotel", 
    label: "Hotel", 
    icon: Building2,
    children: [
      { href: "/admin/hotel?tab=rooms", label: "Habitaciones", icon: Bed },
      { href: "/admin/hotel?tab=deposits", label: "Anticipos", icon: CreditCard },
      { href: "/admin/hotel?tab=surveys", label: "Encuestas", icon: ClipboardCheck },
      { href: "/admin/hotel?tab=calendar", label: "Calendario", icon: CalendarDays },
      { href: "/admin/hotel?tab=cleaning", label: "Limpiezas", icon: FileBarChart },
      { href: "/admin/cupones", label: "Cupones", icon: Ticket },
      { href: "/admin/calendario-sync", label: "Sincronización", icon: RefreshCw },
      { href: "/admin/hotel/channel-manager", label: "Channel Manager", icon: Radio },
    ]
  },
  { 
    href: "/admin/restaurante", 
    label: "Restaurante", 
    icon: UtensilsCrossed,
    children: [
      { href: "/admin/restaurante?tab=reservations", label: "Reservas", icon: CalendarDays },
      { href: "/admin/restaurante?tab=menu", label: "Menú", icon: UtensilsCrossed },
      { href: "/admin/restaurante?tab=print", label: "Impresión de comandas", icon: Printer },
      { href: "/admin/restaurante?tab=inventory", label: "Inventario", icon: ClipboardCheck },
    ]
  },
  { 
    href: "/admin/contabilidad", 
    label: "Contabilidad", 
    icon: FileText,
    children: [
      { href: "/admin/contabilidad?tab=invoices", label: "Facturas", icon: FileText },
      { href: "/admin/contabilidad?tab=income", label: "Ingresos", icon: CreditCard },
      { href: "/admin/contabilidad?tab=expenses", label: "Egresos", icon: ClipboardList },
      { href: "/admin/contabilidad?tab=support-docs", label: "Doc. Soporte", icon: FileText },
      { href: "/admin/contabilidad?tab=gift-cards", label: "Gift Cards", icon: Gift },
      { href: "/admin/contabilidad?tab=payments", label: "Métodos de Pago", icon: CreditCard },
      { href: "/admin/contabilidad?tab=tips", label: "Propinas", icon: CreditCard },
      { href: "/admin/contabilidad?tab=beneficiaries", label: "Beneficiarios", icon: Users },
      { href: "/admin/contabilidad?tab=connectors", label: "Factus", icon: FileText },
    ]
  },
  { href: "/admin/reportes", label: "Reportes Comerciales", icon: BarChart3 },
  { href: "/admin/anomalias", label: "Anomalías", icon: AlertTriangle },
  { href: "/admin/inventario", label: "Inventario", icon: Package },
  { href: "/admin/leads", label: "Leads", icon: UserPlus },
  { href: "/admin/logs", label: "Logs de actividad", icon: ClipboardList },
  { href: "/admin/usuarios", label: "Usuarios", icon: Users },
  { href: "/admin/configuracion", label: "Configuración", icon: Settings },
];

// Items visible to ALL staff
const commonNavItems: NavItem[] = [
  { href: "/admin/mi-cuenta", label: "Mi Cuenta", icon: User },
];

// Role-based navigation filtering
const getNavItemsForRoles = (roles: AppRole[]): NavItem[] => {
  const isAdmin = roles.includes("admin");
  if (isAdmin) return [...allNavItems, ...commonNavItems];

  const isGerencia = roles.includes("gerencia");
  if (isGerencia) {
    const hotelItem = allNavItems.find(item => item.href === "/admin/hotel");
    const filteredHotel = hotelItem ? [{
      ...hotelItem,
      children: hotelItem.children?.filter(c => 
        !["/admin/calendario-sync", "/admin/hotel/channel-manager"].includes(c.href)
      ),
    }] : [];
    const contabilidadItem = allNavItems.find(item => item.href === "/admin/contabilidad");
    const filteredContabilidad = contabilidadItem ? [{
      ...contabilidadItem,
      children: contabilidadItem.children?.filter(c =>
        c.href !== "/admin/contabilidad?tab=connectors"
      ),
    }] : [];
    return [
      { href: "/admin", label: "Recepción", icon: MonitorDot },
      { href: "/admin/estadisticas", label: "Estadísticas", icon: LayoutDashboard },
      ...filteredHotel,
      ...allNavItems.filter(item => item.href === "/admin/restaurante"),
      ...filteredContabilidad,
      { href: "/admin/reportes", label: "Reportes Comerciales", icon: BarChart3 },
      { href: "/admin/inventario", label: "Inventario", icon: Package },
      ...commonNavItems,
    ];
  }

  const isRecepcion = roles.includes("recepcion");
  if (isRecepcion) {
    const hotelItem = allNavItems.find(item => item.href === "/admin/hotel");
    const filteredHotel = hotelItem ? [{
      ...hotelItem,
      children: hotelItem.children?.filter(c => 
        !["/admin/cupones", "/admin/calendario-sync", "/admin/hotel/channel-manager"].includes(c.href)
      ),
    }] : [];
    const contabilidadItem = allNavItems.find(item => item.href === "/admin/contabilidad");
    const filteredContabilidadRecepcion = contabilidadItem ? [{
      ...contabilidadItem,
      children: contabilidadItem.children?.filter(c =>
        c.href === "/admin/contabilidad?tab=beneficiaries"
      ),
    }] : [];
    return [
      { href: "/admin", label: "Recepción", icon: MonitorDot },
      ...filteredHotel,
      ...allNavItems.filter(item => item.href === "/admin/restaurante"),
      ...filteredContabilidadRecepcion,
      ...commonNavItems,
    ];
  }

  const isRRSS = roles.includes("rrss");
  if (isRRSS) {
    // RRSS = mismo acceso que recepción + Gift Cards
    const hotelItem = allNavItems.find(item => item.href === "/admin/hotel");
    const filteredHotel = hotelItem ? [{
      ...hotelItem,
      children: hotelItem.children?.filter(c =>
        !["/admin/cupones", "/admin/calendario-sync", "/admin/hotel/channel-manager"].includes(c.href)
      ),
    }] : [];
    const contabilidadItem = allNavItems.find(item => item.href === "/admin/contabilidad");
    const filteredContabilidadRRSS = contabilidadItem ? [{
      ...contabilidadItem,
      children: contabilidadItem.children?.filter(c =>
        c.href === "/admin/contabilidad?tab=gift-cards"
      ),
    }] : [];
    return [
      { href: "/admin", label: "Recepción", icon: MonitorDot },
      ...filteredHotel,
      ...allNavItems.filter(item => item.href === "/admin/restaurante"),
      ...filteredContabilidadRRSS,
      ...commonNavItems,
    ];
  }

  const isEncuesta = roles.includes("encuesta");
  if (isEncuesta) {
    const hotelItem = allNavItems.find(item => item.href === "/admin/hotel");
    if (hotelItem) {
      return [
        {
          ...hotelItem,
          children: hotelItem.children?.filter(c => c.href === "/admin/hotel?tab=surveys"),
        },
      ];
    }
    return [];
  }

  // Default: dashboard + mi cuenta
  return [
    ...allNavItems.filter(item => item.href === "/admin"),
    ...commonNavItems,
  ];
};

export function AdminLayout() {
  const { user, isLoading, isStaff, signOut, roles } = useAuth();
  const navItems = useMemo(() => getNavItemsForRoles(roles), [roles]);
  const showNotifications = !roles.includes("encuesta");
  const { data: anomalies } = useOpenAnomalies();
  const anomalyCount = anomalies?.critical ?? 0;
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Helper to check if a nav link is active (supports query params)
  const isActiveLink = (href: string) => {
    const [path, query] = href.split("?");
    if (query) {
      // Has query param - check both path and search
      return location.pathname === path && location.search === `?${query}`;
    }
    // No query param - just check pathname (exact match)
    return location.pathname === href;
  };

  // Check if any child is active (for collapsible groups)
  const hasActiveChild = (children?: { href: string }[]) => {
    if (!children) return false;
    return children.some((c) => isActiveLink(c.href)) || 
           children.some((c) => c.href.startsWith(location.pathname) && location.pathname !== "/admin");
  };

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/admin/login");
    } else if (!isLoading && user && !isStaff) {
      navigate("/admin/login");
    }
  }, [isLoading, user, isStaff, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !isStaff) {
    return null;
  }

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background border-b h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src={logo} alt="León Dormido" className="h-8 w-8" />
          <span className="font-semibold">Admin</span>
        </div>
        <div className="flex items-center gap-1">
          {showNotifications && <NotificationBell />}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2"
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-background pt-14">
          <nav className="p-4 space-y-2">
            {navItems.map((item) => (
              <div key={item.href}>
                {item.children ? (
                  <Collapsible defaultOpen={hasActiveChild(item.children)}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium hover:bg-muted">
                      <span className="flex items-center gap-3">
                        <item.icon className="h-5 w-5" />
                        {item.label}
                      </span>
                      <ChevronDown className="h-4 w-4" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4 space-y-1 mt-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={cn(
                            "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors",
                            isActiveLink(child.href)
                              ? "bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          )}
                        >
                          <child.icon className="h-4 w-4" />
                          {child.label}
                        </Link>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                      isActiveLink(item.href)
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                    {item.href === "/admin/anomalias" && anomalyCount > 0 && (
                      <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                        {anomalyCount}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            ))}
            <Separator className="my-4" />
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium hover:bg-muted"
            >
              <Home className="h-5 w-5" />
              Ver sitio web
            </Link>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 w-full"
            >
              <LogOut className="h-5 w-5" />
              Cerrar sesión
            </button>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-card border-r flex-col">
        {/* Logo */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="León Dormido" className="h-10 w-10" />
              <div>
                <p className="font-semibold">León Dormido</p>
                <p className="text-xs text-muted-foreground">Panel Admin</p>
              </div>
            </div>
            {showNotifications && <NotificationBell />}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.href}>
              {item.children ? (
                <Collapsible defaultOpen={hasActiveChild(item.children)}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-muted">
                    <span className="flex items-center gap-3">
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 space-y-1 mt-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className={cn(
                          "flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                          isActiveLink(child.href)
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-muted"
                        )}
                      >
                        <child.icon className="h-4 w-4" />
                        {child.label}
                      </Link>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                <Link
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActiveLink(item.href)
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                  {item.href === "/admin/anomalias" && anomalyCount > 0 && (
                    <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                      {anomalyCount}
                    </span>
                  )}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.email}</p>
              <p className="text-xs text-muted-foreground capitalize">
                {roles.join(", ")}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Button variant="outline" size="sm" className="w-full justify-start gap-2" asChild>
              <Link to="/">
                <Home className="h-4 w-4" />
                Ver sitio web
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        <Suspense fallback={
          <div className="p-6 space-y-6">
            <div className="h-8 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-72 bg-muted animate-pulse rounded" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="h-32 bg-muted animate-pulse rounded-lg" />
              <div className="h-32 bg-muted animate-pulse rounded-lg" />
              <div className="h-32 bg-muted animate-pulse rounded-lg" />
            </div>
            <div className="h-64 bg-muted animate-pulse rounded-lg" />
          </div>
        }>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
